import { useCallback, useEffect } from 'react';
import type { Dispatch } from 'redux';
import { connect } from 'react-redux';
import type { SnackbarTypesType } from 'components-stateless/DesignSystem/Atoms/Snackbar';
import { notify } from 'components-stateless/Notifications';
import { selectNotifications } from './selectors';
import { removeNotification } from './actions';

const NOTIFICATION_TYPE_MAP: {
  [key in StateProps['level']]: SnackbarTypesType;
} = {
  success: 'Success',
  error: 'Error',
  warning: 'Warning',
  info: 'Info',
};

type StateProps = {
  message: string;
  level: 'success' | 'error' | 'warning' | 'info';
  sticky: boolean;
  uid: string;
};

type DispatchProps = {
  removeNotification: (uid: string) => void;
};

type ReduxNotificationHandlerProps = StateProps & DispatchProps;

const ReduxNotificationHandler = ({
  message,
  level,
  sticky,
  uid,
  removeNotification: removeNotificationProp,
}: ReduxNotificationHandlerProps) => {
  const handleClose = useCallback(() => {
    removeNotificationProp(uid);
  }, [uid, removeNotificationProp]);

  useEffect(() => {
    notify({
      htmlMessage: message,
      isCloseable: true,
      type: NOTIFICATION_TYPE_MAP[level],
      onClose: handleClose,
      isSticky: sticky,
    });
  }, [message, level, handleClose, sticky, uid]);

  return null;
};

const mapStateToProps = selectNotifications();

const mapDispatchToProps = (dispatch: Dispatch<IAction>) => ({
  removeNotification: (uid: string) => dispatch(removeNotification(uid)),
});

const ConnectedReduxNotificationHandler = connect<StateProps, DispatchProps>(
  mapStateToProps,
  mapDispatchToProps
)(ReduxNotificationHandler);

export { ConnectedReduxNotificationHandler as ReduxNotificationHandler };
